// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Pickup Requests (from Impact assessments)
// Queue of sites tagged for debris removal, with actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
  Modal, SimpleGrid, Textarea, ActionIcon,
} from '../../components/gab/GabComponents';
import {
  IconRefresh, IconTrash, IconTicket, IconExternalLink,
  IconMapPin, IconCheck, IconX,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { DebrisRequest } from '../../types/debris';

const STATUS_COLORS: Record<string, string> = {
  pending: 'orange', actioned: 'green', dismissed: 'gray',
};

const CATEGORY_COLORS: Record<string, string> = {
  affected: 'green', minor: 'yellow', major: 'orange', destroyed: 'red', inaccessible: 'violet',
};

export default function Requests() {
  const navigate = useNavigate();
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    requests, loadingRequests, loadRequests, updateRequest, deleteRequest,
    createLoadTicket, loadLoadTickets,
  } = useDebrisOpsStore();

  const { zones, contractors, loadZones, loadContractors } = useDebrisStore();

  const [statusFilter, setStatusFilter] = useState<string | null>('pending');
  const [actionTarget, setActionTarget] = useState<DebrisRequest | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisRequest | null>(null);
  const [saving, setSaving] = useState(false);

  // Ticket creation form
  const [ticketForm, setTicketForm] = useState({
    zoneId: '', contractorId: '', debrisType: '', notes: '',
  });

  useEffect(() => {
    loadRequests(); loadZones(); loadContractors();
  }, [loadRequests, loadZones, loadContractors]);

  const iRequests = requests;

  const filtered = useMemo(() => {
    let list = iRequests;
    if (statusFilter && statusFilter !== 'all') list = list.filter(r => r.status === statusFilter);
    return [...list].sort((a, b) => (b.id || 0) - (a.id || 0));
  }, [iRequests, statusFilter]);

  const pendingCount = iRequests.filter(r => r.status === 'pending').length;

  const iZones = incidentId ? zones : [];
  const iContractors = incidentId ? contractors : [];

  const zoneSelect = useMemo(() => iZones.map(z => ({ value: String(z.id), label: z.zoneName || z.zoneCode })), [iZones]);
  const contractorSelect = useMemo(() => iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })), [iContractors]);

  const DEBRIS_TYPES = [
    { value: 'vegetative', label: 'Vegetative' },
    { value: 'c_and_d', label: 'Construction & Demolition' },
    { value: 'soil_mud_sand', label: 'Soil / Mud / Sand' },
    { value: 'hazardous_tree', label: 'Hazardous Trees' },
    { value: 'stump', label: 'Stump Removal' },
    { value: 'white_goods', label: 'White Goods' },
    { value: 'e_waste', label: 'Electronic Waste' },
    { value: 'hhw', label: 'Household Hazardous Waste' },
  ];

  const openAction = (req: DebrisRequest) => {
    setActionTarget(req);
    setTicketForm({ zoneId: '', contractorId: '', debrisType: '', notes: '' });
  };

  const handleCreateTicket = async () => {
    if (!actionTarget) return;
    setSaving(true);
    try {
      // Create load ticket from the request
      const ticket = await createLoadTicket({
        incidentId,
        pickupAddress: actionTarget.address,
        pickupLat: actionTarget.latitude,
        pickupLng: actionTarget.longitude,
        zoneId: ticketForm.zoneId,
        contractorId: ticketForm.contractorId,
        debrisType: ticketForm.debrisType,
        status: 'in_transit',
        notes: `From Impact Assessment #${actionTarget.assessmentId}. ${ticketForm.notes}`.trim(),
      });

      // Mark request as actioned
      await updateRequest(actionTarget.id, {
        status: 'actioned',
        actionedBy: user?.email ?? '',
        actionedAt: new Date().toISOString(),
        createdTicketId: ticket ? String(ticket.id) : '',
      });

      await loadLoadTickets();
      notifications.show({ message: 'Load ticket created and request actioned', color: 'green' });
      setActionTarget(null);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDismiss = async (req: DebrisRequest) => {
    try {
      await updateRequest(req.id, {
        status: 'dismissed',
        actionedBy: user?.email ?? '',
        actionedAt: new Date().toISOString(),
      });
      notifications.show({ message: 'Request dismissed', color: 'gray' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Failed', color: 'red' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteRequest(deleteTarget.id);
      notifications.show({ message: 'Request deleted', color: 'gray' });
      setDeleteTarget(null);
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="sm">
          <Title order={3} c="black">Pickup Requests</Title>
          {pendingCount > 0 && (
            <Badge size="lg" color="orange" variant="filled">{pendingCount} pending</Badge>
          )}
        </Group>
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
          loading={loadingRequests} onClick={() => loadRequests()}>Refresh</Button>
      </Group>

      <Text size="sm" c="dimmed" mb="md">
        Sites tagged from Impact assessments for debris removal. Create a load ticket to action a request.
      </Text>

      <Group mb="md" gap="sm">
        <Select
          data={[{ value: 'all', label: 'All' }, { value: 'pending', label: 'Pending' }, { value: 'actioned', label: 'Actioned' }, { value: 'dismissed', label: 'Dismissed' }]}
          value={statusFilter ?? 'pending'} onChange={v => setStatusFilter(v)} size="xs" w={160}
        />
        <Text size="xs" c="dimmed">{filtered.length} request{filtered.length !== 1 ? 's' : ''}</Text>
      </Group>

      {filtered.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <Text c="dimmed">{loadingRequests ? 'Loading...' : 'No requests found'}</Text>
        </Paper>
      ) : (
        <Stack gap="sm">
          {filtered.map(req => {
            let types: string[] = [];
            try { types = JSON.parse(req.damageTypes || '[]'); } catch { /* */ }
            return (
              <Paper key={req.id} p="md" withBorder radius="sm">
                <Group justify="space-between" align="flex-start">
                  <div style={{ flex: 1 }}>
                    <Group gap="sm" mb={4}>
                      <IconMapPin size={16} color="var(--mantine-color-dimmed)" />
                      <Text fw={600}>{req.address || 'No address'}</Text>
                      <Badge size="sm" color={STATUS_COLORS[req.status] ?? 'gray'}>{req.status}</Badge>
                      <Badge size="sm" color={CATEGORY_COLORS[req.damageCategory] ?? 'gray'} variant="outline">
                        {req.damageCategory || 'N/A'}
                      </Badge>
                    </Group>

                    {req.description && (
                      <Text size="sm" c="dimmed" lineClamp={2} mb={4}>{req.description}</Text>
                    )}

                    <Group gap={4} mb={4}>
                      {types.map(t => (
                        <Badge key={t} size="xs" variant="light">{t.replace(/_/g, ' ')}</Badge>
                      ))}
                    </Group>

                    <Group gap="lg">
                      <Text size="xs" c="dimmed">
                        Tagged by {req.taggedBy || '?'} on {req.taggedAt ? new Date(req.taggedAt).toLocaleDateString() : '?'}
                      </Text>
                      {req.latitude && req.longitude && (
                        <Text size="xs" c="dimmed">
                          {parseFloat(req.latitude).toFixed(4)}, {parseFloat(req.longitude).toFixed(4)}
                        </Text>
                      )}
                      {req.assessmentId && (
                        <Button size="compact-xs" variant="subtle"
                          leftSection={<IconExternalLink size={12} />}
                          onClick={() => navigate(`/impact/assessment/${req.assessmentId}`)}>
                          View Assessment #{req.assessmentId}
                        </Button>
                      )}
                      {req.createdTicketId && (
                        <Text size="xs" c="green" fw={500}>Ticket #{req.createdTicketId} created</Text>
                      )}
                    </Group>
                  </div>

                  {canEdit && req.status === 'pending' && (
                    <Group gap={4}>
                      <Button size="xs" leftSection={<IconTicket size={14} />}
                        onClick={() => openAction(req)}>
                        Create Ticket
                      </Button>
                      <ActionIcon size="sm" variant="subtle" color="gray"
                        onClick={() => handleDismiss(req)} title="Dismiss">
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  )}
                  {canEdit && req.status !== 'pending' && (
                    <ActionIcon size="sm" variant="subtle" color="red"
                      onClick={() => setDeleteTarget(req)} title="Delete">
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Create Ticket Modal */}
      <Modal opened={!!actionTarget} onClose={() => setActionTarget(null)}
        title="Create Load Ticket from Request" centered size="lg">
        {actionTarget && (
          <Stack>
            <Paper p="sm" withBorder radius="sm" style={{ background: 'var(--mantine-color-gray-0)' }}>
              <Group gap="sm">
                <IconMapPin size={16} />
                <Text fw={500}>{actionTarget.address || 'No address'}</Text>
                <Badge size="sm" color={CATEGORY_COLORS[actionTarget.damageCategory] ?? 'gray'}>
                  {actionTarget.damageCategory}
                </Badge>
              </Group>
              {actionTarget.description && (
                <Text size="sm" c="dimmed" mt={4}>{actionTarget.description}</Text>
              )}
            </Paper>

            <SimpleGrid cols={2} spacing="sm">
              <Select label="Zone" data={zoneSelect} value={ticketForm.zoneId || null}
                onChange={v => setTicketForm(p => ({ ...p, zoneId: v ?? '' }))} searchable clearable />
              <Select label="Contractor" data={contractorSelect} value={ticketForm.contractorId || null}
                onChange={v => setTicketForm(p => ({ ...p, contractorId: v ?? '' }))} searchable clearable />
              <Select label="Debris Type" data={DEBRIS_TYPES} value={ticketForm.debrisType || null}
                onChange={v => setTicketForm(p => ({ ...p, debrisType: v ?? '' }))} clearable />
            </SimpleGrid>

            <Textarea label="Notes" value={ticketForm.notes}
              onChange={e => setTicketForm(p => ({ ...p, notes: e.target.value }))} autosize minRows={2} />

            <Group justify="flex-end">
              <Button variant="default" onClick={() => setActionTarget(null)}>Cancel</Button>
              <Button leftSection={<IconCheck size={14} />} loading={saving} onClick={handleCreateTicket}>
                Create Ticket & Action Request
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Request" centered size="sm">
        <Stack>
          <Text size="sm">Delete this request?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
